<script setup>
import { computed, ref } from 'vue';
import { format } from 'date-fns';
import { useMapGetter } from 'dashboard/composables/store';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import WootEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import CardActivityAttachments from './CardActivityAttachments.vue';

const props = defineProps({
  comments: { type: Array, required: true },
  // Resolve to whether the change was saved; see useCardDetail.
  editComment: { type: Function, required: true },
  deleteComment: { type: Function, required: true },
});

// A long thread shows its first comment and its latest ones, and folds the middle away, as
// Notion does. Folding fewer comments than this is not worth a click.
const HEAD = 1;
const TAIL = 3;
const MIN_FOLDED = 3;

const { formatMessage } = useMessageFormatter();
const currentUser = useMapGetter('getCurrentUser');

const isExpanded = ref(false);

const foldedCount = computed(() => {
  const count = props.comments.length - HEAD - TAIL;
  return !isExpanded.value && count >= MIN_FOLDED ? count : 0;
});
const head = computed(() =>
  foldedCount.value ? props.comments.slice(0, HEAD) : props.comments
);
const tail = computed(() =>
  foldedCount.value ? props.comments.slice(-TAIL) : []
);

const isOwn = comment =>
  comment.actor?.type === 'User' && comment.actor.id === currentUser.value.id;

// The comment is the markdown of the private note editor; its @mentions render as names.
const commentHtml = comment => formatMessage(comment.content, false, true);

const formatDate = value => format(new Date(value), "dd/MM/yyyy 'às' HH:mm");

// One comment is edited at a time, in place.
const editingId = ref(null);
const draft = ref('');
const isSaving = ref(false);

const startEdit = comment => {
  editingId.value = comment.id;
  draft.value = comment.content || '';
};

const saveEdit = async comment => {
  isSaving.value = true;
  if (await props.editComment(comment.id, draft.value.trim()))
    editingId.value = null;
  isSaving.value = false;
};

const deleteDialogRef = ref(null);
const deletingId = ref(null);

const askDelete = comment => {
  deletingId.value = comment.id;
  deleteDialogRef.value.open();
};

const confirmDelete = async () => {
  if (await props.deleteComment(deletingId.value))
    deleteDialogRef.value.close();
};
</script>

<template>
  <!-- Mounted with no comments too, so deleting the last one leaves its dialog in place. -->
  <ol v-if="comments.length" class="flex flex-col gap-4 mb-0">
    <template v-for="(group, groupIndex) in [head, tail]" :key="groupIndex">
      <li
        v-if="groupIndex === 1 && foldedCount"
        class="flex items-center gap-3"
      >
        <span class="flex justify-center w-6">
          <span class="w-px h-6 bg-n-weak" />
        </span>
        <button
          type="button"
          class="text-sm text-n-slate-10 hover:text-n-slate-12"
          data-testid="activity-show-more"
          @click="isExpanded = true"
        >
          {{
            $t(
              'CARDS.SHOW.COMMENTS_SHOW_MORE',
              { count: foldedCount },
              foldedCount
            )
          }}
        </button>
      </li>
      <li
        v-for="comment in group"
        :key="comment.id"
        class="flex gap-3 group"
        data-activity-kind="comment"
      >
        <Avatar
          :name="comment.actor?.name || $t('CARDS.HISTORY.SYSTEM')"
          :src="comment.actor?.thumbnail"
          :size="24"
          rounded-full
        />
        <div class="flex flex-col flex-1 min-w-0 gap-1">
          <div class="flex flex-wrap items-center gap-x-2">
            <span
              class="text-sm font-medium text-n-slate-12"
              data-testid="activity-sentence"
            >
              {{ comment.actor?.name || $t('CARDS.HISTORY.SYSTEM') }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{ formatDate(comment.created_at) }}
            </span>
            <span
              v-if="comment.edited_at"
              class="text-xs text-n-slate-10"
              data-testid="comment-edited"
            >
              {{ $t('CARDS.SHOW.COMMENT_EDITED') }}
            </span>
            <!-- Always visible on touch screens, which have no hover. -->
            <div
              v-if="isOwn(comment) && editingId !== comment.id"
              class="flex items-center gap-0.5 ltr:ml-auto rtl:mr-auto sm:opacity-0 sm:group-hover:opacity-100 sm:focus-within:opacity-100"
            >
              <Button
                icon="i-lucide-pencil"
                slate
                ghost
                xs
                :title="$t('CARDS.SHOW.COMMENT_EDIT')"
                data-testid="comment-edit"
                @click="startEdit(comment)"
              />
              <Button
                icon="i-lucide-trash-2"
                slate
                ghost
                xs
                :title="$t('CARDS.SHOW.COMMENT_DELETE')"
                data-testid="comment-delete"
                @click="askDelete(comment)"
              />
            </div>
          </div>
          <div
            v-if="editingId === comment.id"
            class="flex flex-col gap-2"
            data-testid="comment-edit-form"
          >
            <div
              class="px-3 py-1.5 border rounded-lg border-n-weak focus-within:border-n-brand"
              @keydown.esc.stop="editingId = null"
            >
              <WootEditor
                v-model="draft"
                is-private
                :enable-canned-responses="false"
                class="text-sm [&_.ProseMirror-menubar]:!hidden [&_.ProseMirror-woot-style]:!min-h-6 [&_.ProseMirror-woot-style]:!max-h-60 [&_.ProseMirror_p:first-child]:!mt-0"
              />
            </div>
            <div class="flex justify-end gap-2">
              <Button
                :label="$t('CARDS.SHOW.COMMENT_CANCEL')"
                slate
                ghost
                xs
                data-testid="comment-edit-cancel"
                @click="editingId = null"
              />
              <Button
                :label="$t('CARDS.SHOW.COMMENT_SAVE')"
                xs
                :is-loading="isSaving"
                :disabled="!draft.trim() && !comment.attachments?.length"
                data-testid="comment-edit-save"
                @click="saveEdit(comment)"
              />
            </div>
          </div>
          <div
            v-else-if="comment.content"
            v-dompurify-html="commentHtml(comment)"
            class="text-sm break-words text-n-slate-12 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_.prosemirror-mention-node]:font-medium [&_.prosemirror-mention-node]:text-n-blue-11"
            data-testid="activity-comment"
          />
          <CardActivityAttachments
            v-if="comment.attachments?.length"
            :activity="comment"
          />
        </div>
      </li>
    </template>
  </ol>
  <Dialog
    ref="deleteDialogRef"
    type="alert"
    :title="$t('CARDS.SHOW.COMMENT_DELETE_TITLE')"
    :description="$t('CARDS.SHOW.COMMENT_DELETE_DESCRIPTION')"
    :confirm-button-label="$t('CARDS.SHOW.COMMENT_DELETE')"
    @confirm="confirmDelete"
  />
</template>
